import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { EmptyIcon } from 'assets/icon/bigicon';
import {
  fetchMasterConfigs,
  createMasterConfig,
  updateMasterConfig,
} from 'state/actions/masterConfig';
import FieldList from './FieldList';
import AddFieldModal from './AddFieldModal';

const defaultField = {
  fieldType: undefined,
  title: '',
  description: '',
};

function MasterConfigView({
  onBack,
  onPreview,
  draftFields,
  onDraftFieldsChange,
  hasOrderChanged,
  onHasOrderChangedChange,
}) {
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [modalField, setModalField] = useState({ ...defaultField });
  const [editingFieldId, setEditingFieldId] = useState(null);

  const orderedFields = draftFields || [];
  const setOrderedFields = onDraftFieldsChange;
  const setHasOrderChanged = onHasOrderChangedChange;

  const dispatch = useDispatch();
  const { siteId } = useParams();

  const { list, loading } = useSelector((state) => ({
    list: state.masterConfig.list,
    loading: state.masterConfig.loading,
  }));

  useEffect(() => {
    if (siteId) {
      dispatch(fetchMasterConfigs({ siteId }));
    }
  }, [siteId, dispatch]);

  const masterConfig = list?.[0];
  const fields = useMemo(
    () => masterConfig?.fields || [],
    [masterConfig?.fields],
  );

  useEffect(() => {
    if (draftFields === null && fields.length > 0) {
      setOrderedFields(
        [...fields].sort((a, b) => (a.order || 0) - (b.order || 0)),
      );
    }
  }, [fields, draftFields, setOrderedFields]);

  const hasData = orderedFields.length > 0;
  const isEdit = !!editingFieldId;

  const handleOpenAddModal = () => {
    setEditingFieldId(null);
    setModalField({ ...defaultField });
    setShowFieldModal(true);
  };

  const handleOpenEditModal = (field) => {
    setEditingFieldId(field._id);
    setModalField({
      fieldType: field.fieldType,
      title: field.label,
      description: field.description || '',
    });
    setShowFieldModal(true);
  };

  const handleCloseFieldModal = () => {
    setShowFieldModal(false);
    setModalField({ ...defaultField });
    setEditingFieldId(null);
  };

  const handleSubmitField = () => {
    if (!modalField.fieldType || !modalField.title) return;

    if (isEdit) {
      const updated = orderedFields.map((f) =>
        f._id === editingFieldId
          ? {
              ...f,
              fieldType: modalField.fieldType,
              label: modalField.title,
              description: modalField.description,
            }
          : f,
      );
      setOrderedFields(updated);
    } else {
      const newFieldData = {
        fieldType: modalField.fieldType,
        label: modalField.title,
        description: modalField.description,
        order: orderedFields.length + 1,
        _tempId: Date.now(),
      };
      setOrderedFields([...orderedFields, newFieldData]);
    }

    setHasOrderChanged(true);
    handleCloseFieldModal();
  };

  const handleToggleScheduling = (field) => {
    const updated = orderedFields.map((f) => ({
      ...f,
      setAsScheduling: f._id === field._id ? !f.setAsScheduling : false,
    }));

    setOrderedFields(updated);
    setHasOrderChanged(true);
  };

  const handleDeleteField = (field) => {
    const remainingFields = orderedFields
      .filter((f) => (f._id || f._tempId) !== (field._id || field._tempId))
      .map((f, index) => ({ ...f, order: index + 1 }));

    setOrderedFields(remainingFields);
    setHasOrderChanged(true);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const reordered = Array.from(orderedFields);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    const updated = reordered.map((field, index) => ({
      ...field,
      order: index + 1,
    }));

    setOrderedFields(updated);
    setHasOrderChanged(true);
  };

  const handleSubmit = async () => {
    if (!hasOrderChanged) return;

    const fieldsToSave = orderedFields.map(({ _tempId, ...rest }) => rest);

    if (masterConfig) {
      await dispatch(
        updateMasterConfig({
          id: masterConfig._id,
          configName: masterConfig.configName,
          fields: fieldsToSave,
        }),
      );
    } else {
      await dispatch(
        createMasterConfig({
          siteId,
          configName: 'Master Config',
          fields: fieldsToSave,
        }),
      );
    }

    setHasOrderChanged(false);
    setOrderedFields(null);
  };

  return (
    <div className="dynamic-comp">
      <div className="dynamic-comp-header">
        <h2>Master Config</h2>
        <div className="dynamic-comp-header-actions">
          <button
            className="dynamic-comp-btn-new-field"
            onClick={handleOpenAddModal}
          >
            <i className="feather-plus" />
            New Field
          </button>
          <button className="dynamic-comp-btn-back" onClick={onBack}>
            <i className="feather-arrow-left" />
            Back
          </button>
        </div>
      </div>

      {hasData && (
        <FieldList
          fields={orderedFields}
          onDragEnd={handleDragEnd}
          onEdit={handleOpenEditModal}
          onDelete={handleDeleteField}
          onToggleScheduling={handleToggleScheduling}
        />
      )}

      {!hasData && !loading && !hasOrderChanged && (
        <div className="dynamic-comp-empty">
          <EmptyIcon className="dynamic-comp-empty-icon" />
          <h3>No Data</h3>
          <p>
            No fields configured yet. Click "New Field" to start building your
            Master Config.
          </p>
        </div>
      )}

      <div className="dynamic-comp-footer">
        <button className="dynamic-comp-btn-preview" onClick={onPreview}>
          <i className="feather-play-circle" />
          Preview
        </button>
        <button
          className={`dynamic-comp-btn-submit${hasOrderChanged ? ' active' : ''}`}
          disabled={!hasOrderChanged}
          onClick={handleSubmit}
        >
          Submit
        </button>
      </div>

      <AddFieldModal
        visible={showFieldModal}
        field={modalField}
        onChange={setModalField}
        onSubmit={handleSubmitField}
        onCancel={handleCloseFieldModal}
        isEdit={isEdit}
      />
    </div>
  );
}

export default MasterConfigView;